import { Sheet, SheetBody, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { QuoteBuilderPreview } from '@/components/quotes/quote-builder-preview'
import { summarizeQuoteBuilderEvents } from '@/components/quotes/quote-builder-events-summary'
import { getQuotePaymentScheduleAmountCents } from '@/components/quotes/quote-builder-payment-card'
import type { QuoteLineItem } from '@/components/quotes/quote-line-items-table'
import { DiscountTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { calculateLineTotalCents } from '@/lib/line-item-calculations'
import { formatQuotePaymentScheduleDue } from '@/lib/quote-payment-schedule-defaults'
import type { QuoteEditorFormValues, QuoteEditorTotals } from './quote-editor-form'

interface QuoteEditorPreviewSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  recipientFirstName: string
  values: QuoteEditorFormValues
  /** Every line on the quote, transportation included. */
  lineItems: QuoteLineItem[]
  totals: QuoteEditorTotals
}

const validUntilFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

/** What the customer sees in the portal, rendered from the editor's current values. */
export function QuoteEditorPreviewSheet({ open, onOpenChange, recipientFirstName, values, lineItems, totals }: QuoteEditorPreviewSheetProps) {
  const events = summarizeQuoteBuilderEvents(values.events)
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Customer preview</SheetTitle>
        </SheetHeader>
        <SheetBody>
          <QuoteBuilderPreview
            recipientFirstName={recipientFirstName}
            title={values.title}
            validUntilLabel={values.validUntil ? validUntilFormatter.format(values.validUntil) : undefined}
            eventsSummary={events.hasEvents ? [events.countLabel, events.detail].filter(Boolean).join(' · ') : undefined}
            lineItems={lineItems.map((item, index) => ({
              key: item.id || item.tempId || `item-${index}`,
              name: item.name,
              amountCents: calculateLineTotalCents(item.quantity, item.unitPriceCents),
              quantity: item.quantity,
              unitPriceCents: item.unitPriceCents,
              unit: item.unit,
              salesTaxRate: item.salesTaxRate,
              isTaxable: item.isTaxable,
              salesTaxCents: item.salesTaxCents,
            }))}
            addonOffers={values.addonOffers.map((offer, index) => ({
              key: offer.id || offer.tempId || `addon-${index}`,
              name: offer.name,
              amountCents: calculateLineTotalCents(offer.quantity, offer.unitPriceCents),
              quantity: offer.quantity,
              unitPriceCents: offer.unitPriceCents,
              unit: offer.unit,
              salesTaxRate: offer.salesTaxRate,
              isTaxable: offer.isTaxable,
            }))}
            subtotalCents={totals.subtotalCents}
            discountAmountCents={totals.discountCents}
            discountLabel={
              values.discountType === DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE && values.discountValue
                ? `Discount (${values.discountValue}%)`
                : 'Discount'
            }
            taxAmountCents={totals.salesTaxCents}
            tipCents={totals.enforcedTipCents}
            tipLabel={values.enforcedTipPercentage ? `Enforced tip (${values.enforcedTipPercentage}%)` : undefined}
            totalCents={totals.totalCents}
            schedules={values.paymentSchedules.map((schedule, index) => ({
              key: `${schedule.sequenceNumber}-${index}`,
              label: `${schedule.description || `Payment ${index + 1}`} — ${formatQuotePaymentScheduleDue(schedule).toLowerCase()}`,
              amountCents: getQuotePaymentScheduleAmountCents(schedule, totals.totalCents),
            }))}
          />
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}
